import {
  Avatar,
  Center,
  HStack,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { PlacementStatusBadge } from '@duke-hq/ui/components/PlacementStatusBadge/PlacementStatusBadge';
import { emptyPaginationResponse } from '@duke-hq/lib/pagination';
import { getUserFullName, nth } from '@duke-hq/lib';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Plural, Trans } from '@lingui/react/macro';
import { useSearchParams } from 'react-router-dom';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import {
  PlacementStatusFilter,
  usePlacementStatusFilter,
} from './components/PlacementStatusFilter';

export function PlacementsStudents(): React.JSX.Element {
  const { pageSize, currentPage, setPage } = usePagination();
  const [searchParams] = useSearchParams();
  const querySearch = (searchParams.get('search') ?? '').trim();
  const statuses = usePlacementStatusFilter();

  const { data, isFetching } = trpcTenant.workPlacements.listStudents.useQuery(
    {
      search: querySearch === '' ? undefined : querySearch,
      filters: statuses.length > 0 ? { statuses } : undefined,
      pagination: { page: currentPage, limit: pageSize },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  return (
    <TableViewLayout
      pageHeader={[
        <PageHeading key="page-heading">
          <Trans>Placements</Trans>
        </PageHeading>,
      ]}
      filters={
        <>
          <SearchInput maxW="search-input" />
          <PlacementStatusFilter />
        </>
      }
      tableActionsLeft={
        <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
          <Plural
            value={data.pagination.totalCount || 0}
            zero="0 students"
            one="1 student"
            other="# students"
          />
        </Text>
      }
      table={
        <>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>
                  <Trans>Student</Trans>
                </Th>
                <Th>
                  <Trans>Status</Trans>
                </Th>
                <Th>
                  <Trans>Course</Trans>
                </Th>
                <Th>
                  <Trans>Semester</Trans>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.items.map((row) => (
                <Tr key={row.enrolmentId}>
                  <Td>
                    <HStack gap={3}>
                      <Avatar
                        size="sm"
                        name={getUserFullName(row.student)}
                        src={row.student.picture_url ?? undefined}
                      />
                      <Stack gap={0}>
                        <Text>{getUserFullName(row.student)}</Text>
                        {row.student.email != null ? (
                          <Text fontSize="xs" color="fg.subtle">
                            {row.student.email}
                          </Text>
                        ) : null}
                      </Stack>
                    </HStack>
                  </Td>
                  <Td>{row.status != null ? <PlacementStatusBadge status={row.status} /> : '—'}</Td>
                  <Td>{row.course?.title ?? '—'}</Td>
                  <Td>{row.semester == null ? '—' : nth(row.semester)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {isFetching ? (
            <Center p={8}>
              <Spinner size="xl" />
            </Center>
          ) : data.items.length === 0 ? (
            <Center p={8}>
              <Text textAlign="center">
                {querySearch !== '' || statuses.length > 0 ? (
                  <Trans>No students found.</Trans>
                ) : (
                  <Trans>No students with placements yet</Trans>
                )}
              </Text>
            </Center>
          ) : null}
        </>
      }
      pagination={
        data.items.length > 0 ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} />
        ) : null
      }
    />
  );
}
